import { ComponentProps, FC, useCallback, useEffect, useRef, useState } from 'react';
import PinInput from 'react-pin-field';

import { Button } from '../../Button/Button';
import { CircularProgress } from '../../CircularProgress/CircularProgress';
import { FormikOnErrorTracking } from '../FormikOnErrorTracking/FormikOnErrorTracking';
import styles from './FormikTokenInput.module.scss';

type FormikTokenInputProps = Partial<ComponentProps<typeof FormikOnErrorTracking>> & {
  onResendTokenVerificationCode: () => Promise<void>;
  onSubmit: () => Promise<void>;
  onChange: (value: any) => void;
  forceShowResendButton?: boolean;
  invalidTokenMessage?: string;
  isDisabled: boolean;
  inputLength: number;
  timerLength: number;
};

export const FormikTokenInput: FC<FormikTokenInputProps> = ({
  onResendTokenVerificationCode,
  forceShowResendButton,
  invalidTokenMessage,
  inputLength,
  timerLength,
  onChange,
  isDisabled,
  onError,
  onSubmit,
}) => {
  const isMounted = useRef(true);
  const [timer, setTimer] = useState<number>(timerLength);
  const [isSendingCode, setIsSendingCode] = useState<boolean>(false);
  const timeOutCallback = useCallback(() => setTimer((currTimer) => currTimer - 1), []);
  const [isSubmittingCode, setIsSubmittingCode] = useState<boolean>(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    if (timer > 0) {
      timeoutId = setTimeout(timeOutCallback, 1000);
    }
    return () => timeoutId && clearTimeout(timeoutId);
  }, [timer, timeOutCallback]);

  const onResend = () => {
    setIsSendingCode(true);
    onResendTokenVerificationCode().then(() => {
      // Check if the component is mounted
      if (isMounted.current) {
        setTimer(timerLength);
        setIsSendingCode(false);
      }
    });
  };

  const handleOnCompleteInput = () => {
    setIsSubmittingCode(true);
    onSubmit().then(() => {
      // Check if the component is mounted
      if (isMounted.current) {
        setIsSubmittingCode(false);
      }
    });
  };

  const inputstyles = [styles.verificationTokenFormContainer];
  if (invalidTokenMessage) {
    inputstyles.push(styles.verificationTokenFormContainerError);
  }

  return (
    <div className={styles.verifyTokenForm}>
      {!!onError && <FormikOnErrorTracking onError={onError} />}
      <div className={inputstyles.join(' ')}>
        <PinInput
          name="verificationCode"
          validate={/^[0-9]$/}
          autoFocus
          length={inputLength}
          onChange={onChange}
          onComplete={handleOnCompleteInput}
          disabled={isDisabled}
          data-testid="signup-verification-code"
        />
        {invalidTokenMessage && <div className={styles.verificationCodeError}>{invalidTokenMessage}</div>}
      </div>
      {!isSubmittingCode && (
        <div className={styles.resendCode}>
          {timer <= 0 || forceShowResendButton ? (
            <Button
              onClick={onResend}
              className={styles.resendCodeButton}
              variant="tertiary"
              isLoading={isSendingCode}
              disabled={isSendingCode || isDisabled}
              data-testid="resendCodeButton"
              size="small"
            >
              Resend code
            </Button>
          ) : (
            <div className={styles.resendCodeText}>Code sent. Resend in {timer} seconds</div>
          )}
        </div>
      )}
      {isSubmittingCode && (
        <div className={styles.loadingContainer}>
          <div className={styles.progressContainer}>
            <CircularProgress />
          </div>
          <div className={styles.loadingText}>We are verifying your account</div>
        </div>
      )}
    </div>
  );
};
